import * as React from "react";

import { cn } from "./utils";

type InputVariant = 'default' | 'transparent' | 'secondary';

interface InputProps extends React.ComponentProps<"input"> {
  variant?: InputVariant;
  showCounter?: boolean;
  maxLength?: number;
  /**
   * Текст ошибки (для управления aria-invalid).
   * Если задан непустой — Input помечается как невалидный (красная рамка через CSS,
   * скринридер озвучивает ошибку). Сам текст ошибки отображать снаружи.
   */
  error?: string | undefined;
}

const Input = React.forwardRef<HTMLInputElement, InputProps>(
  ({ className, type, variant = 'default', showCounter = false, maxLength, value, error, ...props }, ref) => {
    const variantClasses: Record<InputVariant, string> = {
      default: 'shadow-[var(--shadow)] bg-input-background',
      transparent: 'border-0 bg-transparent',
      secondary: 'border-none shadow-none bg-secondary',
    };

    const currentLength = typeof value === 'string' ? value.length : 0;
    const remainingLength = maxLength ? maxLength - currentLength : 0;
    const isOverLimit = maxLength ? currentLength > maxLength : false;

    const inputElement = (
      <input
        type={type}
        data-slot="input"
        aria-invalid={error ? true : undefined}
        className={cn(
          "file:text-foreground placeholder:text-muted-foreground/50 placeholder:font-normal selection:bg-primary-light selection:text-foreground flex w-full min-w-0 rounded-[var(--radius)] transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-normal disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50",
          "h-12 px-4 py-1 text-base text-foreground font-semibold",
          // Скрываем стрелки у number-инпутов
          "[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none",
          variant !== 'transparent' && "aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40",
          variantClasses[variant],
          className,
        )}
        ref={ref}
        value={value}
        maxLength={maxLength}
        {...props}
      />
    );

    if (showCounter) {
      return (
        <div className="relative">
          {inputElement}
          <span className={cn(
            "absolute right-4 top-1/2 -translate-y-1/2 text-xs font-normal tabular-nums",
            isOverLimit ? "text-destructive" : "text-muted-foreground/50"
          )}>
            {remainingLength}
          </span>
        </div>
      );
    }

    return inputElement;
  }
);

Input.displayName = "Input";

export { Input };
export type { InputProps, InputVariant };